import { useCallback, useEffect, useRef, useState } from "react";
import {
	navIndexToXmlStage,
	preValidateXmlSectionsFromData,
} from "../utils/pre-validate-xml-sections";
import { CREDIT_REISSUE_CONSTANTS } from "../utils/credit-reissue-constants";

const CREDIT_REISSUE_STAGE = 3;

const useLoanApplicationNavigation = ({
	loanApplicationStage,
	setLoanApplicationStage,
	loanGuid,
}) => {
	const sectionHandlersRef = useRef({});
	const previousStageRef = useRef(loanApplicationStage);
	const navigationClickRef = useRef(false);
	const completedStagesRef = useRef(new Set());
	const shouldHighlightOnEnterRef = useRef(false);
	const highlightedStagesRef = useRef(new Set());
	const incompleteSectionsRef = useRef(new Set());
	const creditProvidedRef = useRef("");
	const stageRegisterFnsRef = useRef({});
	const registerSectionHandlerRef = useRef(null);
	const [incompleteSections, setIncompleteSections] = useState(() => new Set());
	const [completedSections, setCompletedSections] = useState(() => new Set());
	const [isNavigating, setIsNavigating] = useState(false);

	useEffect(() => {
		incompleteSectionsRef.current = incompleteSections;
	}, [incompleteSections]);

	const stageToNavIndex = useCallback((stage) => {
		return stage === 0 ? 0 : stage - 1;
	}, []);

	const tryHighlightSectionErrors = useCallback(
		(stage) => {
			if (!shouldHighlightOnEnterRef.current) {
				return;
			}

			if (stage !== loanApplicationStage) {
				return;
			}

			if (!incompleteSectionsRef.current.has(stageToNavIndex(stage))) {
				return;
			}

			if (highlightedStagesRef.current.has(stage)) {
				return;
			}

			const handlers = sectionHandlersRef.current[stage];

			if (!handlers?.highlightErrors) {
				return;
			}

			highlightedStagesRef.current.add(stage);
			handlers.highlightErrors();
		},
		[loanApplicationStage, stageToNavIndex]
	);

	const navIndexToStage = useCallback(
		(navIndex) => {
			if (navIndex === 0 && loanGuid) {
				return 1;
			}

			return navIndex + 1;
		},
		[loanGuid]
	);

	const getProgressCurrentStep = useCallback(() => {
		return loanApplicationStage === 0 ? 0 : loanApplicationStage - 1;
	}, [loanApplicationStage]);

	const markSectionCompletion = useCallback(
		(stage, isComplete) => {
			const navIndex = stageToNavIndex(stage);

			if (isComplete) {
				completedStagesRef.current.add(stage);
			} else {
				completedStagesRef.current.delete(stage);
			}

			setIncompleteSections((prev) => {
				if (isComplete && !prev.has(navIndex)) {
					return prev;
				}

				if (!isComplete && prev.has(navIndex)) {
					return prev;
				}

				const next = new Set(prev);

				if (isComplete) {
					next.delete(navIndex);
				} else {
					next.add(navIndex);
				}

				incompleteSectionsRef.current = next;
				return next;
			});

			setCompletedSections((prev) => {
				if (isComplete && prev.has(navIndex)) {
					return prev;
				}

				if (!isComplete && !prev.has(navIndex)) {
					return prev;
				}

				const next = new Set(prev);

				if (isComplete) {
					next.add(navIndex);
				} else {
					next.delete(navIndex);
				}

				return next;
			});
		},
		[stageToNavIndex]
	);

	const setCreditProvidedMode = useCallback((value) => {
		creditProvidedRef.current = value || "";
	}, []);

	const registerSectionHandler = useCallback(
		(stage, handlers) => {
			if (handlers) {
				sectionHandlersRef.current[stage] = handlers;
				tryHighlightSectionErrors(stage);

				if (
					loanGuid &&
					handlers.validate?.() &&
					!completedStagesRef.current.has(stage)
				) {
					markSectionCompletion(stage, true);
				}
			}
		},
		[loanGuid, markSectionCompletion, tryHighlightSectionErrors]
	);

	const preValidateXmlSections = useCallback((applicationData, options = {}) => {
		if (!loanGuid) {
			return;
		}

		const completedNavIndices = preValidateXmlSectionsFromData(
			applicationData,
			options
		);

		setCompletedSections((prev) => {
			const next = new Set(prev);
			completedNavIndices.forEach((navIndex) => next.add(navIndex));
			return next;
		});

		setIncompleteSections((prev) => {
			const next = new Set(prev);
			completedNavIndices.forEach((navIndex) => next.delete(navIndex));
			return next;
		});

		completedNavIndices.forEach((navIndex) => {
			completedStagesRef.current.add(navIndexToXmlStage(navIndex));
		});
	}, [loanGuid]);

	const resetSectionProgress = useCallback(() => {
		setIncompleteSections(new Set());
		setCompletedSections(new Set());
		incompleteSectionsRef.current = new Set();
		completedStagesRef.current = new Set();
		creditProvidedRef.current = "";
		shouldHighlightOnEnterRef.current = false;
		highlightedStagesRef.current.clear();
	}, []);

	const handleNavigateToStage = useCallback(
		async (navIndex) => {
			const targetStage = navIndexToStage(navIndex);

			if (targetStage === loanApplicationStage || isNavigating) {
				return;
			}

			setIsNavigating(true);

			try {
				const handlers = sectionHandlersRef.current[loanApplicationStage];

				if (handlers) {
					const isValid = handlers.validate ? handlers.validate() : true;
					let saveSucceeded = true;

					// Always persist on progress-bar navigate (auto-save), even if invalid.
					// Completion/red state still depends on validation + save result.
					if (handlers.save) {
						const saveResult = await handlers.save();
						saveSucceeded = saveResult !== false;
					}

					markSectionCompletion(
						loanApplicationStage,
						isValid && saveSucceeded
					);
				}

				for (let s = loanApplicationStage + 1; s < targetStage; s++) {
					if (
						s === CREDIT_REISSUE_STAGE &&
						creditProvidedRef.current === CREDIT_REISSUE_CONSTANTS.MANUAL
					) {
						markSectionCompletion(s, true);
						continue;
					}

					if (!completedStagesRef.current.has(s)) {
						markSectionCompletion(s, false);
					}
				}

				window.scrollTo({
					top: 0,
					left: 0,
					behavior: "smooth",
				});
				navigationClickRef.current = true;
				setLoanApplicationStage(targetStage);
			} finally {
				setIsNavigating(false);
			}
		},
		[
			isNavigating,
			loanApplicationStage,
			markSectionCompletion,
			navIndexToStage,
			setLoanApplicationStage,
		]
	);

	useEffect(() => {
		const previousStage = previousStageRef.current;

		if (previousStage === loanApplicationStage) {
			return;
		}

		if (navigationClickRef.current) {
			navigationClickRef.current = false;
		} else if (loanApplicationStage > previousStage) {
			// Mark every stage we leave or skip (e.g. Manual jumps Address → Employment
			// and must fill Credit Reissue as complete).
			for (let s = previousStage; s < loanApplicationStage; s++) {
				markSectionCompletion(s, true);
			}
		}

		previousStageRef.current = loanApplicationStage;
		highlightedStagesRef.current.delete(loanApplicationStage);
		tryHighlightSectionErrors(loanApplicationStage);
	}, [loanApplicationStage, markSectionCompletion, tryHighlightSectionErrors]);

	const validateAllSectionsForSubmit = useCallback(
		    (loanSteps = []) => {
			const invalidNavIndices = new Set([...incompleteSections]);
			const expectedStages = loanGuid
				? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
				: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

			expectedStages.forEach((stage) => {
				if (
					stage === CREDIT_REISSUE_STAGE &&
					creditProvidedRef.current === CREDIT_REISSUE_CONSTANTS.MANUAL
				) {
					invalidNavIndices.delete(stageToNavIndex(stage));
					return;
				}

				const handlers = sectionHandlersRef.current[stage];

				if (!handlers?.validate) {
					return;
				}
				if (!handlers.validate()) {
					invalidNavIndices.add(stageToNavIndex(stage));
				}
			});

			const incompleteSectionNames = [...invalidNavIndices]
				.sort((a, b) => a - b)
				.map((navIndex) => loanSteps[navIndex])
				.filter(Boolean);

			if (invalidNavIndices.size > 0) {
				const nextIncompleteSections = new Set(invalidNavIndices);
				setIncompleteSections(nextIncompleteSections);
				incompleteSectionsRef.current = nextIncompleteSections;
				shouldHighlightOnEnterRef.current = true;
				highlightedStagesRef.current.clear();
			} else {
				setIncompleteSections(new Set());
				incompleteSectionsRef.current = new Set();
				shouldHighlightOnEnterRef.current = false;
				highlightedStagesRef.current.clear();
			}

			return {
				isValid: invalidNavIndices.size === 0,
				incompleteSectionNames,
			};
		},
		[incompleteSections, loanGuid, stageToNavIndex]
	);

	const makeRegisterForStage = useCallback((stage) => {
		if (!stageRegisterFnsRef.current[stage]) {
			stageRegisterFnsRef.current[stage] = (handlers) => {
				registerSectionHandlerRef.current?.(stage, handlers);
			};
		}

		return stageRegisterFnsRef.current[stage];
	}, []);

	registerSectionHandlerRef.current = registerSectionHandler;

	return {
		handleNavigateToStage,
		registerSectionHandler,
		makeRegisterForStage,
		incompleteSections,
		completedSections,
		isNavigating,
		preValidateXmlSections,
		progressCurrentStep: getProgressCurrentStep(),
		resetSectionProgress,
		validateAllSectionsForSubmit,
		markSectionCompletion,
		setCreditProvidedMode,
	};
};

export default useLoanApplicationNavigation;
